export type SharedCalendarListStatus = 'PENDING' | 'SUCCESS' | 'FAILURE';

export interface SharedCalendarItem {
  user_id: number;
  provider_calendar_id: string;
  name: string;
  is_primary: boolean;
  is_default: boolean;
}

export interface SharedCalendarPollResults {
  calendars: SharedCalendarItem[];
}

export interface SharedCalendarTriggerFetchResponse {
  data: { fetch_uuid: string };
  errors: string[];
}

export interface SharedCalendarListPollResponse {
  data: {
    status: SharedCalendarListStatus;
    results?: SharedCalendarPollResults;
  };
  errors: string[];
}

export interface UseGetSharedCalendarOptions {
  /** Passed to `list` as `type` (e.g. `saved`, `unsaved`). */
  listType?: string;
}

const FETCH_ERROR_MESSAGES = {
  failure: 'Unable to load the available shared calendars. Try again later.',
  maxAttempts:
    'Unable to load the available shared calendars. Try again later.',
  missingUuid: 'Could not start shared calendar sync. Try again later.',
} as const;

export type SharedCalendarFetchErrorReason =
  | 'api_errors'
  | 'failure_status'
  | 'max_attempts'
  | 'missing_fetch_uuid';

export class SharedCalendarFetchError extends Error {
  readonly reason: SharedCalendarFetchErrorReason;

  readonly apiErrors: string[];

  constructor(
    reason: SharedCalendarFetchErrorReason,
    message?: string,
    apiErrors: string[] = []
  ) {
    super(message ?? FETCH_ERROR_MESSAGES.failure);
    this.name = 'SharedCalendarFetchError';
    this.reason = reason;
    this.apiErrors = apiErrors;
  }
}

export function getToastMessageForFetchError(err: unknown): string {
  if (err instanceof SharedCalendarFetchError) {
    if (err.reason === 'missing_fetch_uuid') {
      return FETCH_ERROR_MESSAGES.missingUuid;
    }
    if (err.reason === 'max_attempts') {
      return FETCH_ERROR_MESSAGES.maxAttempts;
    }
    if (err.reason === 'failure_status') {
      return FETCH_ERROR_MESSAGES.failure;
    }
    if (err.apiErrors.length > 0) {
      return err.apiErrors.join(', ');
    }
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return FETCH_ERROR_MESSAGES.failure;
}
